/**
 * Native secure auth-token storage (src/platform/).
 *
 * Contract: every capability has a working web implementation; native
 * branches (via isNativePlatform()) load Capacitor plugins dynamically and
 * MUST keep the web fallback. On native, tokens live in the OS
 * Keychain/Keystore via @aparajita/capacitor-secure-storage, never the
 * WebView's localStorage. Web is a complete no-op here — `api/client.ts`'s
 * tokenManager keeps using localStorage directly on web.
 */

import { isNativePlatform } from './detection'

const ACCESS_TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'

export interface NativeTokens {
  accessToken: string | null
  refreshToken: string | null
}

/** Reads both tokens from native secure storage. Empty on web or on any failure. */
export async function readNativeTokens(): Promise<NativeTokens> {
  if (!isNativePlatform()) return { accessToken: null, refreshToken: null }
  try {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage')
    const [accessToken, refreshToken] = await Promise.all([
      SecureStorage.getItem(ACCESS_TOKEN_KEY),
      SecureStorage.getItem(REFRESH_TOKEN_KEY),
    ])
    return { accessToken, refreshToken }
  } catch {
    return { accessToken: null, refreshToken: null }
  }
}

/** Writes both tokens to native secure storage. No-op on web. */
export async function writeNativeTokens(accessToken: string, refreshToken: string): Promise<void> {
  if (!isNativePlatform()) return
  try {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage')
    await Promise.all([
      SecureStorage.setItem(ACCESS_TOKEN_KEY, accessToken),
      SecureStorage.setItem(REFRESH_TOKEN_KEY, refreshToken),
    ])
  } catch {
    // Best-effort — the in-memory cache still holds the tokens for this
    // session; a failed persist just means re-auth is needed on next launch.
  }
}

/** Removes both tokens from native secure storage. No-op on web. */
export async function clearNativeTokens(): Promise<void> {
  if (!isNativePlatform()) return
  try {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage')
    await Promise.all([
      SecureStorage.removeItem(ACCESS_TOKEN_KEY),
      SecureStorage.removeItem(REFRESH_TOKEN_KEY),
    ])
  } catch {
    // Best-effort.
  }
}
